import { create } from 'zustand';
import type {
  CandidateWordsPayload,
  CandidateWordItem,
  ErrorPayload,
  GameEndPayload,
  IndicatorBroadcastPayload,
  IndicatorEvent,
  MatchResultPayload,
  PhaseChangePayload,
  PlayerPublic,
  PrivateRolePayload,
  ServerMessage,
  SessionSnapshot,
  StartRequestPayload,
  WaitlistEntry,
  WordPickStartedPayload,
  WordPickedPayload,
  SwipeRecordedPayload,
  Phase,
  Role,
  BaseRole,
  GameEndResult,
} from '@/protocol/types';
import type { ConnectionStatus } from '@/protocol/wsClient';

export interface SelfState {
  token: string;
  nickname: string;
  ready: boolean;
  role: Role | null;
  baseRole: BaseRole | null;
  isChief: boolean;
  privateWord: string | null;
  teammates: string[] | null;
  seerToken: string | null;
}

export interface RecentError {
  code: string;
  message?: string;
  ts: number;
  inReplyTo?: string;
}

export interface SessionStoreState {
  // 连接
  connectionStatus: ConnectionStatus;
  storageMode: 'local' | 'session' | 'memory';
  // 玩家
  self: SelfState | null;
  players: PlayerPublic[];
  waitlist: WaitlistEntry[];
  // 阶段
  phase: Phase;
  phaseStartedAt: number;
  phaseDeadlineAt: number | null;
  // 对局
  match: {
    matchId: number;
    startedAt: number;
    indicators: IndicatorEvent[];
    chiefCorrectDeclared: boolean;
  } | null;
  // 大厅开局请求
  startRequest: StartRequestPayload | null;
  // 终局结算
  result: MatchResultPayload | null;
  // 最近一次指示物广播（驱动全屏动画）
  lastIndicator: IndicatorBroadcastPayload | null;
  // 反击：本人已提交
  wolfAccusationSubmitted: boolean;
  // 最近错误（toast）
  lastError: RecentError | null;
  // session reset 通知
  sessionResetReason: string | null;

  /** v1.1：当前房间号（4 位数字），未在房间时为 null */
  roomCode: string | null;

  // ───── v1 新增 ─────
  /** 村长 token（word_pick / playing 阶段服务端会告知） */
  chiefToken: string | null;
  /** 候选词数量（公开信息） */
  candidateCount: number;
  /** 候选词列表（仅村长收到）——v2.1 改为结构化 {id, text} */
  candidateWords: CandidateWordItem[] | null;
  /** 选中的词（仅知情者可见） */
  word: string | null;
  /** 已记录的 swipe 数 */
  swipeCount: number;
  /** 终局结果 */
  endResult: GameEndResult | null;
  /** 终局身份揭示 */
  revealedRoles: Array<{ token: string; nickname: string; baseRole: BaseRole; isChief: boolean }>;
  /** 返回大厅倒计时 ts */
  returnLobbyDeadlineTs: number | null;
  // 开局取消原因 toast (START_REQUEST_CANCELLED.payload.reason)
  startCancelReason: string | null;
  /** v2.1 hotfix: 本端是否已点过 「我已看完」 ack，仅本地反馈，不依赖后端广播 */
  selfAcked: boolean;
}

export interface SessionStoreActions {
  setConnectionStatus: (s: ConnectionStatus) => void;
  setStorageMode: (m: 'local' | 'session' | 'memory') => void;
  setRoomCode: (code: string | null) => void;
  setInitialIdentity: (token: string, nickname: string) => void;
  applyServerMessage: (msg: ServerMessage) => void;
  /** 视图层用：清除全屏动画 */
  clearLastIndicator: () => void;
  /** 视图层用：清最近错误 */
  clearLastError: () => void;
  /** 视图层用：清 reset 通知 */
  clearSessionReset: () => void;
  /** 视图层用：清开局取消 toast */
  clearStartCancelReason: () => void;
  /** 本地立即清屏（NightView 自我保护，PRD §7.3） */
  clearPrivateInfo: () => void;
  /** v2.1 hotfix: 本地记录玩家点了 ack */
  setSelfAcked: (v: boolean) => void;
  /** 重置所有状态（测试） */
  __reset: () => void;
}

const initial: SessionStoreState = {
  connectionStatus: 'idle',
  storageMode: 'local',
  self: null,
  players: [],
  waitlist: [],
  phase: 'lobby',
  phaseStartedAt: 0,
  phaseDeadlineAt: null,
  match: null,
  startRequest: null,
  result: null,
  lastIndicator: null,
  wolfAccusationSubmitted: false,
  lastError: null,
  sessionResetReason: null,
  roomCode: null,
  chiefToken: null,
  candidateCount: 0,
  candidateWords: null,
  word: null,
  swipeCount: 0,
  endResult: null,
  revealedRoles: [],
  returnLobbyDeadlineTs: null,
  startCancelReason: null,
  selfAcked: false,
};

export const useSessionStore = create<SessionStoreState & SessionStoreActions>((set) => ({
  ...initial,

  setConnectionStatus: (s) => set({ connectionStatus: s }),
  setStorageMode: (m) => set({ storageMode: m }),
  setSelfAcked: (v) => set({ selfAcked: v }),
  setRoomCode: (code) => set({ roomCode: code }),

  setInitialIdentity: (token, nickname) =>
    set((s) => ({
      self: s.self
        ? { ...s.self, token, nickname }
        : {
            token,
            nickname,
            ready: false,
            role: null,
            baseRole: null,
            isChief: false,
            privateWord: null,
            teammates: null,
            seerToken: null,
          },
    })),

  applyServerMessage: (msg) => applyMsg(set, msg),

  clearLastIndicator: () => set({ lastIndicator: null }),
  clearLastError: () => set({ lastError: null }),
  clearSessionReset: () => set({ sessionResetReason: null }),
  clearStartCancelReason: () => set({ startCancelReason: null }),
  clearPrivateInfo: () =>
    set((s) => ({
      self: s.self
        ? {
            ...s.self,
            role: null,
            baseRole: null,
            isChief: false,
            privateWord: null,
            teammates: null,
            seerToken: null,
          }
        : null,
      word: null,
      candidateWords: null,
    })),

  __reset: () => set({ ...initial }),
}));

// ───────── reducer ─────────

type SetFn = (
  partial:
    | Partial<SessionStoreState & SessionStoreActions>
    | ((
        state: SessionStoreState & SessionStoreActions,
      ) => Partial<SessionStoreState & SessionStoreActions>),
) => void;

function applyMsg(set: SetFn, msg: ServerMessage): void {
  switch (msg.type) {
    case 'STATE_SNAPSHOT': {
      const p = msg.payload as SessionSnapshot;
      // P0-1 fix：backend snapshot 带了 isChief/baseRole——断线重连后要能恢复。
      // 原实现硬写 false/null，导致村长重连后丢身份。以 snapshot 为准，
      // snapshot 未携带时 fallback 到当前 state（不无脑覆盖）。
      set((s) => ({
        self: {
          token: p.self.token,
          nickname: p.self.nickname,
          ready: p.self.ready,
          role: p.self.role,
          baseRole: p.self.baseRole ?? s.self?.baseRole ?? null,
          isChief: p.self.isChief ?? s.self?.isChief ?? false,
          privateWord: p.self.privateWord,
          teammates: p.self.teammates,
          seerToken: p.self.seerToken,
        },
        players: p.players,
        waitlist: p.waitlist,
        phase: p.phase,
        phaseStartedAt: p.phaseStartedAt,
        phaseDeadlineAt: p.phaseDeadlineAt,
        match: p.match,
        startRequest: p.startRequest,
        result: null,
        wolfAccusationSubmitted: false,
        sessionResetReason: null,
      }));
      return;
    }
    case 'RESUME_OK':
      return;
    case 'PLAYER_JOIN': {
      const p = msg.payload as {
        token: string;
        nickname: string;
        joinedWaitlist: boolean;
      };
      set((s) => {
        if (p.joinedWaitlist) {
          if (s.waitlist.some((w) => w.token === p.token)) return s;
          return {
            waitlist: [...s.waitlist, { token: p.token, nickname: p.nickname, joinedAt: msg.ts }],
          };
        }
        if (s.players.some((pl) => pl.token === p.token)) return s;
        return {
          players: [
            ...s.players,
            {
              token: p.token,
              nickname: p.nickname,
              ready: false,
              online: true,
              isChief: false,
              inMatch: false,
            },
          ],
        };
      });
      return;
    }
    case 'PLAYER_LEAVE': {
      const p = msg.payload as { token: string };
      set((s) => ({
        players: s.players.map((pl) =>
          pl.token === p.token ? { ...pl, online: false } : pl,
        ),
        waitlist: s.waitlist.filter((w) => w.token !== p.token),
      }));
      return;
    }
    case 'PLAYER_UPDATE': {
      const p = msg.payload as Partial<PlayerPublic> & { token: string };
      set((s) => {
        const players = s.players.map((pl) =>
          pl.token === p.token ? { ...pl, ...p } : pl,
        );
        const self =
          s.self && s.self.token === p.token && p.nickname != null
            ? { ...s.self, nickname: p.nickname }
            : s.self;
        return { players, self };
      });
      return;
    }
    case 'READY_UPDATE': {
      const p = msg.payload as { readyTokens: string[]; totalNeeded: number };
      const set2 = new Set(p.readyTokens);
      set((s) => ({
        players: s.players.map((pl) => ({ ...pl, ready: set2.has(pl.token) })),
        self: s.self ? { ...s.self, ready: set2.has(s.self.token) } : null,
      }));
      return;
    }
    case 'START_REQUEST_BROADCAST': {
      set({ startRequest: msg.payload as StartRequestPayload });
      return;
    }
    case 'START_REQUEST_CANCELLED': {
      const reason =
        (msg.payload as { reason?: string } | undefined)?.reason ?? 'unknown';
      set({ startRequest: null, startCancelReason: reason });
      return;
    }
    case 'PHASE_CHANGE': {
      const p = msg.payload as PhaseChangePayload;
      set((s) => {
        const next: Partial<SessionStoreState> = {
          phase: p.phase,
          phaseStartedAt: p.startedAt,
          phaseDeadlineAt: p.deadlineTs,
        };
        // P1 fix：v0 里 phase 叫 'night'；v1 拆为 role_reveal / word_pick / playing。
        // 任一对局中 phase 且带 matchId 都初始化 match 对象。
        if (
          (p.phase === 'role_reveal' ||
            p.phase === 'word_pick' ||
            p.phase === 'playing') &&
          p.matchId != null
        ) {
          next.match = {
            matchId: p.matchId,
            startedAt: p.startedAt,
            indicators: [],
            chiefCorrectDeclared: false,
          };
        }
        if (p.phase === 'lobby') {
          // 进 lobby 立即清私密
          if (s.self) {
            next.self = {
              ...s.self,
              role: null,
              baseRole: null,
              isChief: false,
              privateWord: null,
              teammates: null,
              seerToken: null,
              ready: false,
            };
          }
          next.match = null;
          next.result = null;
          next.wolfAccusationSubmitted = false;
          next.startRequest = null;
          next.chiefToken = null;
          next.candidateCount = 0;
          next.candidateWords = null;
          next.word = null;
          next.swipeCount = 0;
          next.endResult = null;
          next.revealedRoles = [];
          next.returnLobbyDeadlineTs = null;
          next.selfAcked = false;
        }
        if (p.phase === 'role_reveal') {
          next.swipeCount = 0;
          next.endResult = null;
          next.revealedRoles = [];
          next.candidateWords = null;
          next.word = null;
          next.selfAcked = false;
        }
        return next;
      });
      return;
    }
    case 'PRIVATE_ROLE': {
      const p = msg.payload as PrivateRolePayload;
      set((s) => ({
        self: s.self
          ? {
              ...s.self,
              role: p.role,
              baseRole: p.baseRole ?? (p.role === 'chief' ? null : (p.role as BaseRole)),
              isChief: p.isChief ?? (p.role === 'chief'),
              privateWord: p.word,
              teammates: p.teammates,
              seerToken: p.seerToken,
            }
          : null,
        word: p.word ?? s.word,
      }));
      return;
    }
    // ───── v1 新增 ─────
    case 'START_GAME_TRIGGERED':
    case 'START_GAME_BROADCAST': {
      // 只是提示作用，阶段由随后的 PHASE_CHANGE 推动
      return;
    }
    case 'WORD_PICK_STARTED': {
      const p = msg.payload as WordPickStartedPayload;
      set({
        chiefToken: p.chiefToken,
        candidateCount: p.candidateCount,
        candidateWords: null,
        word: null,
      });
      return;
    }
    case 'CANDIDATE_WORDS': {
      const p = msg.payload as CandidateWordsPayload;
      const list: CandidateWordItem[] = (p.candidates ?? []).map((c) => ({
        id: c.id,
        text: c.text,
      }));
      set((s) => ({
        candidateWords: list,
        candidateCount: p.candidateCount ?? list.length,
        chiefToken: p.chiefToken ?? s.chiefToken,
      }));
      return;
    }
    case 'WORD_PICKED': {
      const p = msg.payload as WordPickedPayload;
      set((s) => ({
        chiefToken: p.chiefToken ?? s.chiefToken,
        word: p.word ?? s.word,
        candidateWords: null,
      }));
      return;
    }
    case 'SWIPE_RECORDED': {
      const p = msg.payload as SwipeRecordedPayload;
      set({ swipeCount: p.swipeCount });
      return;
    }
    case 'GAME_END': {
      const p = msg.payload as GameEndPayload;
      set({
        endResult: p.result,
        revealedRoles: p.revealedRoles ?? [],
        word: p.word ?? null,
        returnLobbyDeadlineTs: p.returnLobbyDeadlineTs ?? null,
      });
      return;
    }
    case 'CHIEF_REVISED': {
      // 仅作为事件提示；UI 不依赖
      return;
    }
    case 'INDICATOR_BROADCAST': {
      const p = msg.payload as IndicatorBroadcastPayload;
      set((s) => {
        if (!s.match) return s;
        const evt: IndicatorEvent = {
          eventId: msg.eventId,
          ts: p.ts,
          targetToken: p.targetToken,
          indicator: p.indicator,
        };
        return {
          match: { ...s.match, indicators: [...s.match.indicators, evt] },
          lastIndicator: p,
        };
      });
      return;
    }
    case 'CHIEF_CORRECT_DECLARED': {
      set((s) =>
        s.match
          ? { match: { ...s.match, chiefCorrectDeclared: true } }
          : s,
      );
      return;
    }
    case 'WOLF_ACCUSE_PRIVATE_ACK': {
      set({ wolfAccusationSubmitted: true });
      return;
    }
    case 'MATCH_RESULT': {
      // P0-2 fix：backend transitions.ts endMatch() 实发的是 MATCH_RESULT。
      // 原实现只存 v0 字段 state.result，EndView 读不到 → 结算页永远“本局已中止/无身份信息”。
      // 现在从 backend 真实字段派生 EndView 读的 endResult/revealedRoles/word/returnLobbyDeadlineTs。
      const p = msg.payload as MatchResultPayload;
      const revealedRoles = (p.roles ?? []).map((r) => {
        const isChief = r.token === p.chiefToken;
        // chief 实际 baseRole 后端不会发顶层 role（role==='chief'）——
        // 现阶段兑 villager，身份贴以 isChief 高亮；与顶层 role 是另一维。
        const baseRole: BaseRole =
          r.role === 'wolf' || r.role === 'seer' || r.role === 'guard' || r.role === 'villager'
            ? r.role
            : 'villager';
        return {
          token: r.token,
          nickname: r.nickname,
          baseRole,
          isChief,
        };
      });
      set({
        endResult: p.result,
        revealedRoles,
        word: p.word ?? null,
        returnLobbyDeadlineTs: p.returnLobbyDeadlineTs ?? null,
        chiefToken: p.chiefToken ?? null,
        // 保留 result 原始 payload供后续 D4 玫瑰图 / D5 verdict UI 读 swipeStats
        result: p,
      });
      return;
    }
    case 'SESSION_RESET': {
      const p = msg.payload as { reason: string };
      // 清本地全部对局状态；等下一帧 SNAPSHOT 覆盖
      set((s) => ({
        match: null,
        result: null,
        startRequest: null,
        wolfAccusationSubmitted: false,
        sessionResetReason: p.reason,
        self: s.self
          ? {
              ...s.self,
              role: null,
              privateWord: null,
              teammates: null,
              seerToken: null,
              ready: false,
            }
          : null,
      }));
      return;
    }
    case 'ERROR': {
      const p = msg.payload as ErrorPayload;
      set({
        lastError: { code: p.code, message: p.message, ts: msg.ts, inReplyTo: p.inReplyTo },
      });
      return;
    }
    case 'ACK':
    case 'PONG':
      return;
    default:
      return;
  }
}
